import React from "react";
import DialogBox from "../../util/DialogBox";
import DialogBoxWithConfirmation from "../../util/DialogBoxWithConfirmation";
import { useTranslation } from "react-i18next";
import "../../style/Marker.css";
import { useState } from "react";
import { roadmakerApi } from "../../../api/roadmakerApi";
import { normalizeFileName } from "../../util/Util";
import {
  discardPoiMediaUploads,
  pendingPoiMediaUploadsSnapshot,
  republishMarkerMedia,
} from "../../../api/poiMediaUploads";
import { findServerMediaByName, resolveMediaSource } from "./mediaSource";
import { findActiveImageByIdentity, normalizeMediaName } from "../../../domain/mainImage";

const MarkerImage = ({
  markerImage,
  marker,
  setMarkerImage,
  markerImageName,
  setMarkerImageName,
  // Constat 5 (2e revue du 01/10) : séparation nom brut / affichage.
  // rawImageNames = noms BRUTS alignés sur markerImage/imageIds — utilisés
  // pour le callback onChooseMainImage, la suppression et la résolution ;
  // markerImageName (alias displayImageNames) = libellés AFFICHÉS (homonymes
  // suffixés « (2) ») — aria compris. RessourceMarkerForm fournit les deux ;
  // markerImageName seul reste accepté (rendu de consultation).
  rawImageNames,
  setRawImageNames,
  mainImage,
  imageIds,
  onChooseMainImage,
  onRemoveImage,
  setMarkers,
}) => {
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Source rétrocompatible centralisée dans ./mediaSource : valeur locale
  // ou legacy complète telle quelle → média serveur authentifié → URL
  // GitHub reconstruite uniquement pour un nom sans référence complète.
  // Noms BRUTS du formulaire : la prop dédiée quand le parent sépare brut
  // et affichage (édition), sinon repli sur markerImageName (consultation,
  // où brut == affichage).
  const rawNameAt = (index) =>
    rawImageNames?.[index] ?? markerImageName?.[index];

  const mediaNameAt = (index) =>
    rawNameAt(index) ?? marker.imageName?.[index];

  const serverMediaAt = (index) =>
    findServerMediaByName(marker, "image", mediaNameAt(index));

  const imageSource = (image, index) =>
    resolveMediaSource(marker, "image", { value: image, index });

  // F2/F4/F6 + constat 3 (2e revue du 01/10) : quand l'IDENTITÉ de
  // l'entrée est connue, la résolution serveur se fait UNIQUEMENT par
  // elle (checksum exact, homonymes distingués) — JAMAIS de fallback par
  // nom, qui supprimerait le mauvais média serveur pour une entrée legacy
  // homonyme. Le fallback nom ne s'applique que si l'identité est ABSENTE
  // (consultation sans tableau parallèle).
  const serverMediaForIdentity = (index) => {
    const identity = imageIds?.[index];
    if (!identity) return serverMediaAt(index);
    return findActiveImageByIdentity(marker.media, identity) || undefined;
  };

  // Constat f18 (revue finale du 01/10) : un média serveur SANS checksum
  // (interdit par le schéma normal mais un résidu peut exister) reçoit
  // une identité de repli "server-<nom>". Pour ne pas le voir
  // ressusciter indéfiniment : correspondance de nom UNIQUE parmi les
  // images actives → c'est lui, supprimable ; ambiguë (plusieurs
  // homonymes actifs sans checksum) → impossible de désigner le bon,
  // l'utilisateur est informé et RIEN n'est retiré.
  const serverMediaWithoutChecksumForIdentity = (index, identity) => {
    if (!identity || !identity.startsWith("server-")) return undefined;
    const name = mediaNameAt(index);
    if (!name) return undefined;
    const candidates = (marker.media || []).filter(
      (media) =>
        media &&
        media.mediaType === "image" &&
        media.status !== "deleting" &&
        !media.checksum &&
        (normalizeMediaName(media.originalName) === normalizeMediaName(name) ||
          normalizeMediaName(media.fileName) === normalizeMediaName(name))
    );
    return candidates.length === 1 ? candidates[0] : undefined;
  };

  // Nombre d'images actives sans checksum portant ce nom (tolérant).
  const countActiveImagesWithoutChecksumForName = (name) => {
    if (!name) return 0;
    return (marker.media || []).filter(
      (media) =>
        media &&
        media.mediaType === "image" &&
        media.status !== "deleting" &&
        !media.checksum &&
        (normalizeMediaName(media.originalName) === normalizeMediaName(name) ||
          normalizeMediaName(media.fileName) === normalizeMediaName(name))
    ).length;
  };

  // Pièce IMAGE en file portant ce nom (brut ou normalisé) — une pièce
  // dont le contentKey figure dans imageIds appartient à sa PROPRE
  // entrée du formulaire, pas à une entrée legacy homonyme (défaut B).
  const pendingImageForName = (markerId, name, idList = imageIds) => {
    const knownKeys = new Set(idList || []);
    const items = pendingPoiMediaUploadsSnapshot(markerId);
    return (items || []).some(
      (item) =>
        item &&
        item.mediaType === "image" &&
        !knownKeys.has(item.contentKey) &&
        (item.file?.name === name ||
          (typeof item.file?.name === "string" &&
            normalizeFileName(item.file?.name) === name))
    );
  };

  // Constat f10 (revue finale du 01/10) : une entrée LEGACY (identité
  // "legacy-…") sans média serveur ni pièce en file sur un POI DÉJÀ
  // sauvegardé ne peut PAS être supprimée : la retirer localement
  // paraît réussir puis elle REVIENT au rechargement (le champ legacy
  // n'est jamais purgé). Vrai → il faut informer l'utilisateur.
  const isLegacyNonRemovable = (index, identity, serverMedia) => {
    if (serverMedia) return false;
    if (!identity || !identity.startsWith("legacy-")) return false;
    if (!marker.serverId) return false; // POI jamais sauvegardé : retrait local OK
    // Une pièce en file correspondante ? Le retrait local est valide
    // (onRemoveImage l'abandonnera par contentKey) — mais une identité
    // legacy ne contient PAS de contentKey : il faut regarder la file
    // par nom de fichier.
    const name = mediaNameAt(index);
    if (!name) return true;
    return !pendingImageForName(marker.id, name);
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === marker.image.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? marker.image.length - 1 : prevIndex - 1
    );
  };

  /**
   * Permet de lancer la suppression d'une image du marqueur
   * @param {integer} index - Index de l'image à supprimer du marqueur
   */
  const handleDeleteImage = (index) => async (e) => {
    e.stopPropagation();
    const confirmDelete = await DialogBoxWithConfirmation({
      title: t("deleteImages"),
      text: t("deleteImagesText"),
      icon: "warning",
      cancelButtonText: t("no"),
      confirmButtonText: t("yes"),
    });

    // Confirmer la suppression
    if (confirmDelete) {
      // Suppression serveur si le média est persisté (ADR 0005) ; un
      // média local non encore sauvé est seulement retiré de l'état.
      const identity = imageIds?.[index];
      let serverMedia = serverMediaForIdentity(index);
      // Constat f18 : identité "server-<nom>" (média sans checksum) —
      // suppression seulement si la correspondance de nom est UNIQUE.
      if (!serverMedia) {
        serverMedia = serverMediaWithoutChecksumForIdentity(index, identity);
      }
      // Constat f10 : legacy non supprimable → informer, ne rien retirer.
      if (!serverMedia && isLegacyNonRemovable(index, identity)) {
        DialogBox({
          text: t("legacyImageInfo"),
          icon: "info",
          confirmButtonText: "OK",
        });
        return;
      }
      // Constat f18 : ambigu — informer, ne rien retirer.
      if (
        !serverMedia &&
        identity &&
        identity.startsWith("server-") &&
        countActiveImagesWithoutChecksumForName(mediaNameAt(index)) > 1
      ) {
        DialogBox({
          text: t("serverImageAmbiguousInfo"),
          icon: "info",
          confirmButtonText: "OK",
        });
        return;
      }
      if (serverMedia) {
        if (!marker.serverId || !marker.Project_id) return;
        try {
          await roadmakerApi.poiMedia.remove(
            marker.Project_id,
            marker.serverId,
            serverMedia
          );
        } catch (error) {
          console.error("Échec de la suppression du média serveur :", error);
          return;
        }
        marker.media = (marker.media || []).filter(
          (media) => media.fileName !== serverMedia.fileName
        );
        // La consultation doit refléter la suppression sans rechargement.
        republishMarkerMedia(marker, setMarkers);
      }
      // Resserrement du 01/10 (constat 3) : quand le parent fournit
      // onRemoveImage, l'abandon de la pièce en file lui est DÉLÉGUÉ —
      // l'abandonner ici aussi serait un DOUBLE DISCARD. On appelle donc
      // UNIQUEMENT le parent après l'éventuelle suppression serveur, et
      // on s'arrête là.
      if (onRemoveImage) {
        onRemoveImage(index);
        return;
      }
      // Repli local complet (SANS prop parent) : abandon par contentKey/
      // identité exacte quand elle est connue — JAMAIS par nom à ce moment-là
      // (deux fichiers homonymes de contenus différents restent distincts).
      // Le fallback par nom ne s'applique qu'aux entrées sans identité
      // (legacy sans tableau parallèle). L'identité a déjà été lue plus
      // haut dans ce gestionnaire (constat f18) : on la réutilise.
      const name = mediaNameAt(index);
      discardPoiMediaUploads(
        marker.id,
        (item) =>
          item.mediaType === "image" &&
          (identity
            ? item.contentKey === identity
            : item.file.name === name ||
              normalizeFileName(item.file.name) === name)
      );
      setMarkerImage(markerImage.filter((_, i) => i !== index));
      if (setRawImageNames) {
        setRawImageNames(rawImageNames.filter((_, i) => i !== index));
      } else {
        setMarkerImageName(markerImageName.filter((_, i) => i !== index));
      }
    }
  };

  return (
    <div>
      {markerImage &&
        markerImage.map((image, index) => (
          <div key={index}>
            <div>{markerImageName && markerImageName[index]}</div>
            <img
              alt="Image téléchargée"
              src={imageSource(markerImage[index], index)}
              className="imageMarker"
            />
            <br />
            {(markerImage.length > 1 ||
              (markerImage.length === 1 && index === 0)) && (
              <>
                <button
                  type="button"
                  className="buttonDeleteFile me-2"
                  onClick={handleDeleteImage(index)}
                  aria-label="delete"
                >
                  <i className="bi bi-trash"></i>
                </button>
                {/* Lot « image principale » (2026-10-01, F9) : étoile pleine
                    pour l'image principale, contour pour les autres ; un clic
                    désigne directement l'image. aria-pressed annonce l'état,
                    aria-label est LISIBLE (nom du fichier, suffixé en cas
                    d'homonymes) — jamais le checksum. Affichée seulement à
                    partir de 2 images : à moins, rien à choisir. */}
                {markerImage.length >= 2 &&
                  imageIds &&
                  imageIds.length > index &&
                  onChooseMainImage && (
                    <button
                      type="button"
                      className="buttonEditFile mt-3 mb-3"
                      onClick={() =>
                        onChooseMainImage(
                          imageIds[index],
                          // Constat 5 : le callback reçoit le nom BRUT —
                          // mainResource doit rester un vrai nom de fichier,
                          // jamais le libellé suffixé.
                          rawNameAt(index)
                        )
                      }
                      aria-pressed={mainImage === imageIds[index] ? "true" : "false"}
                      aria-label={`${t("mainImageStar")} : ${
                        // Constat 5 : l'aria affiche le libellé SUFFIXÉ.
                        markerImageName?.[index] ?? ""
                      }`}
                      title={t("mainImageStar")}
                    >
                      <i
                        className={
                          mainImage === imageIds[index]
                            ? "bi bi-star-fill"
                            : "bi bi-star"
                        }
                      ></i>
                    </button>
                  )}
              </>
            )}
          </div>
        ))}
      <div style={{ alignItems: "center", position: "relative" }}>
        {marker.image && !markerImage && marker.image.length > 0 && (
          <div>
            <div className="text-markerName">
              {marker.imageName && marker.imageName[currentImageIndex]}
            </div>
            <img
              alt="Image téléchargée"
              src={imageSource(marker.image?.[currentImageIndex], currentImageIndex)}
              className="imageMarker"
            />
            <br />
            <button
              type="button"
              onClick={handlePreviousImage}
              className="arrowButton left"
            >
              <i className="bi bi-arrow-left"></i>
            </button>
            <button
              type="button"
              onClick={handleNextImage}
              className="arrowButton right"
            >
              <i className="bi bi-arrow-right"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarkerImage;
